import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Phone, PackageSearch } from "lucide-react";
import { API, useShop } from "@/shop/ShopContext";
import { CATEGORIES, VEHICLES } from "@/lib/catalog";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Catalog = () => {
  const { config } = useShop();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const category = params.get("categoria") || "";
  const vehicle = params.get("veicolo") || "";

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (category && p.category !== category) return false;
      if (vehicle && !p.vehicles.includes(vehicle)) return false;
      if (needle && !`${p.name} ${p.code} ${p.vehicles.join(" ")}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [products, q, category, vehicle]);

  const setCategory = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("categoria", id);
    else next.delete("categoria");
    setParams(next);
  };

  const setVehicle = (v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set("veicolo", v);
    else next.delete("veicolo");
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-14" data-testid="catalog-page">
      <Reveal>
        <p className="font-display text-xs uppercase tracking-[0.4em] text-[#FF5500]">Catalogo completo</p>
        <h1 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-neutral-100 sm:text-6xl">
          Ricambi in pronta consegna
        </h1>
        <p className="mt-4 max-w-2xl text-base text-neutral-400">
          Filtra per categoria o per modello. Non trovi il tuo pezzo? Chiama il{" "}
          <a href={config.phone_href} className="font-semibold text-[#FF5500]" data-testid="catalog-phone-link">
            {config.phone}
          </a>{" "}
          e lo ordiniamo noi per te.
        </p>
      </Reveal>

      <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <input
            data-testid="search-parts-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, codice OEM o modello (es. SH 150, pastiglie, batteria...)"
            className="w-full rounded-lg border border-white/10 bg-[#1C2028] py-3.5 pl-11 pr-4 text-sm text-neutral-100 placeholder:text-neutral-500 focus:border-[#FF5500] focus:outline-none"
          />
        </div>
        <Select value={vehicle || "all"} onValueChange={setVehicle}>
          <SelectTrigger
            data-testid="vehicle-filter-select"
            className="w-full border-white/10 bg-[#1C2028] py-3.5 text-sm text-neutral-100 lg:w-72"
          >
            <SelectValue placeholder="Filtra per veicolo" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#1C2028] text-neutral-100">
            <SelectItem value="all">Tutti i veicoli</SelectItem>
            {VEHICLES.map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="category-filter-btn">
        <button
          onClick={() => setCategory("")}
          data-testid="category-chip-all"
          className={`rounded-full px-4 py-2 font-display text-xs uppercase tracking-[0.15em] transition-colors ${
            !category
              ? "bg-[#FF5500] text-white"
              : "border border-white/15 text-neutral-300 hover:border-[#FF5500]"
          }`}
        >
          Tutte
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id === category ? "" : c.id)}
            data-testid={`category-chip-${c.id}`}
            className={`rounded-full px-4 py-2 font-display text-xs uppercase tracking-[0.15em] transition-colors ${
              category === c.id
                ? "bg-[#FF5500] text-white"
                : "border border-white/15 text-neutral-300 hover:border-[#FF5500]"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <p className="mt-8 font-mono text-xs uppercase tracking-widest text-neutral-500" data-testid="results-count">
        {loading ? "Caricamento..." : `${filtered.length} ricambi trovati`}
      </p>

      {loading ? (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-72 animate-pulse rounded-xl border border-white/10 bg-[#14171D]" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-4 rounded-xl border border-white/10 bg-[#14171D] py-16 text-center" data-testid="no-results">
          <PackageSearch className="h-12 w-12 text-neutral-600" />
          <p className="text-neutral-400">Nessun ricambio trovato con questi filtri.</p>
          <a
            href={config.phone_href}
            className="flex items-center gap-2 rounded-full bg-[#FF5500] px-6 py-3 font-display text-sm uppercase tracking-[0.2em] text-white"
            data-testid="no-results-phone"
          >
            <Phone className="h-4 w-4" /> Chiedi al negozio: {config.phone}
          </a>
        </div>
      ) : (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {filtered.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Catalog;
